/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useBookingCalendarLink } from "../lib/react-query/useBookingCalendarLink";

const formatDateForCalendar = (dateTime) => {
  const date = new Date(dateTime);
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  const adjustedDate = new Date(date.getTime() - timezoneOffset);

  return adjustedDate.toISOString().replace(/[-:]/g, "").slice(0, -5);
};

const createCalendarLink = {
  google: (data) => {
    const start = formatDateForCalendar(data.start);
    const end = formatDateForCalendar(data.end);

    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      data.title,
    )}&details=${encodeURIComponent(data.description)}&location=${encodeURIComponent(
      data.location,
    )}&dates=${start}/${end}`;
  },

  yahoo: (data) => {
    const start = formatDateForCalendar(data.start);
    const end = formatDateForCalendar(data.end);

    return `https://calendar.yahoo.com/?v=60&TITLE=${encodeURIComponent(
      data.title,
    )}&ST=${start}&ET=${end}&DESC=${encodeURIComponent(data.description)}&in_loc=${encodeURIComponent(data.location)}`;
  },
};

export const useCalendarLinks = (bookings, bookingEntities, onlineMeetingLink) => { 
  const firstBooking = bookings[0];
  const lastBooking = bookings[bookings.length - 1];

  const {
    refetch: fetchBookingCalendarLink,
    data: calendarFileLink,
    isLoading: isCalendarLinkLoading,
    isError: isCalendarLinkError,
  } = useBookingCalendarLink(firstBooking?.id, {
    enabled: false,
  });
  const { service, provider, location } = bookingEntities;
  const isLoading = isCalendarLinkLoading;
  const isError = isCalendarLinkError;

  const event = useMemo(() => {
    return {
      title: service.name,
      description: `You have been booked for ${service.name} with ${provider.name}. ${
        onlineMeetingLink ? `\n\nClient meeting link: ${onlineMeetingLink}` : ""
      }`,
      location: location?.full_address || "",
      start: firstBooking?.start_datetime,
      end: lastBooking?.end_datetime,
    };
  }, [firstBooking, lastBooking, service, provider, location, onlineMeetingLink]);

  const calendarLinks = useMemo(
    () => ({
      google: createCalendarLink.google(event),
      yahoo: createCalendarLink.yahoo(event),
      apple: calendarFileLink,
      outlook: calendarFileLink,
    }),
    [event, calendarFileLink],
  );

  return { calendarLinks, isLoading, isError, fetchBookingCalendarLink };
};
